import React, { useState, useEffect, createContext } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { useGetTeamsQuery } from 'common/store/modules/api/afl-api';
import {
    useGetBFAwardsLeaderboardQuery,
    useGetBrownlowBettingOddsQuery
} from 'common/store/modules/api/cfs-api';

import {
    leaderboardFavouriteSelector,
    leaderboardFilterSelector
} from 'common/store/modules/player-awards/leaderboard/selectors';

import { LeaderboardTable } from './LeaderboardTable';

import { usePagination } from '../../../hooks/use-fetch-more-data';
import { useFilteredData } from '../../../hooks/use-filters';
import { useBettingVis } from 'common/react/hooks/use-betting-vis';
import { useLeaderboardRefetch } from '../../../hooks/use-leaderboard-refetch';
import { useGetCurrentRoundNumber } from '../../../hooks/use-find-current-round';
import { navSelector } from 'common/store/modules/competition-nav/selectors';
import { leaderboardSelector } from 'common/store/modules/player-awards/leaderboard/selectors/simple-selectors';

/**
 * @constant {number} PAGE_SIZE Number of items to show per page
 */
const PAGE_SIZE = 15;

/**
 * @constant {number} LEADER_ROUND_DELAY How many rounds until we start
 * to highlight the leader
 */
const LEADER_ROUND_DELAY = 3;

export const SeasonContext = createContext('');
export const CurrentRoundContext = createContext('');

export const LeaderboardTableFetcher = ({
    shouldHidePredicted,
    title,
    startDateTime
}) => {
    const [data, setData] = useState([]);

    const nav = useSelector(navSelector);
    const seasons = nav?.seasons;
    const season = seasons?.find((item) => item.id === nav.season.id);
    const seasonPid = season?.providerId ?? '';

    const leaderboardState = useSelector(leaderboardSelector);

    const currentRoundNumber = useGetCurrentRoundNumber(season?.providerId);

    // Fetch teams data
    const { data: teamsData } = useGetTeamsQuery(season?.id, {
        skip: !season?.id ? true : false
    });

    const teams = teamsData || [];
    const team = teams.find((item) => item.id === leaderboardState.team.id);
    const teamId = team?.providerId ?? '';

    // Fetch leaderboard data
    const {
        data: leaderboardData,
        isFetching: isLeaderboardFetching,
        isUninitialized: isLeaderboardUninitialized
    } = useGetBFAwardsLeaderboardQuery(
        {
            seasonId: seasonPid
        },
        {
            skip: !(seasonPid && seasonPid !== '')
        }
    );

    useLeaderboardRefetch(
        seasonPid,
        PULSE.app.common.CONSTANTS.TIME.THIRTY_SECS_IN_MS,
        startDateTime
    );

    const leaderboardFilter = useSelector(leaderboardFilterSelector);
    let filteredData = useFilteredData(data, leaderboardFilter, teamId);

    // Use pagination hook to manage pagination and fetching more data
    const [
        shownData,
        fetchMoreData,
        currentPage,
        setShownData,
        setCurrentPage
    ] = usePagination(leaderboardData?.leaderboard, PAGE_SIZE);

    useEffect(() => {
        setCurrentPage(1);
    }, [season]); // Reset current page when season changes

    // Get the status of the leaderboard data
    const status = leaderboardData?.status
        ? PULSE.app.common.match.getMatchStatus(leaderboardData.status)
        : '';

    const favourites = useSelector(leaderboardFavouriteSelector);
    const mappedFavourites = favourites.map((player) => player.providerId);

    // filter out any favourites not in our filtered data
    const filteredFavourites = mappedFavourites.filter((item) => {
        const found = filteredData.find((filteredItem) => {
            return item === filteredItem.player.playerId;
        });

        return found ? item : false;
    });

    const favouritesToMerge = shownData
        ? shownData
              .filter((item) =>
                  filteredFavourites.includes(item.player.playerId)
              )
              .map((favouriteItem) => {
                  return {
                      ...favouriteItem,
                      roundByRoundVotes:
                          filteredData.find(
                              (item) =>
                                  item.player.playerId ===
                                  favouriteItem.player.playerId
                          )?.roundByRoundVotes ||
                          favouriteItem.roundByRoundVotes
                  };
              })
        : [];

    const preparedFavouritesToMerge = [];
    favouritesToMerge.forEach((item) => {
        if (!teamId || (teamId && item.team.teamId === teamId)) {
            preparedFavouritesToMerge.push({
                ...item,
                player: {
                    ...item.player,
                    leader: item.player.leader ?? false,
                    winner: item.player.winner ?? false
                }
            });
        }
    });

    // set the shown data based on the filtered results
    useEffect(() => {
        const merged = [
            ...preparedFavouritesToMerge,
            ...filteredData.slice(0, PAGE_SIZE * currentPage)
        ];

        const mergedTableAndFavs = Array.from(
            new Map(merged.map((item) => [item.player.playerId, item])).values()
        );

        setShownData(mergedTableAndFavs);
    }, [filteredData, setShownData, currentPage]);

    // Handle showing the leader based on match status and current round
    const [showLeader, setShowLeader] = useState(false);

    useEffect(() => {
        if (!team) {
            if (status === PULSE.app.common.CONSTANTS.MATCH_STATUS.LIVE) {
                // Check if more than 3 rounds
                if (currentRoundNumber >= LEADER_ROUND_DELAY) {
                    setShowLeader(true);
                } else {
                    setShowLeader(false);
                }
            } else if (
                status === PULSE.app.common.CONSTANTS.MATCH_STATUS.COMPLETED
            ) {
                setShowLeader(true);
            }
        }
    }, [status, team, leaderboardData]);

    // Handle betting odds data
    const { data: bettingOddsData } = useGetBrownlowBettingOddsQuery(null, {
        pollingInterval: PULSE.app.common.CONSTANTS.TIME.ONE_MINUTE_IN_MS
    });

    const shouldShowBettingOdds =
        useBettingVis() &&
        status !== PULSE.app.common.CONSTANTS.MATCH_STATUS.COMPLETED &&
        bettingOddsData?.displayChannels?.web === true &&
        bettingOddsData?.outcomes?.length > 0;

    return (
        <SeasonContext.Provider value={season}>
            <CurrentRoundContext.Provider value={currentRoundNumber}>
                <LeaderboardTable
                    title={title}
                    shouldHidePredicted={shouldHidePredicted}
                    leaderboardData={leaderboardData}
                    setData={setData}
                    filteredData={filteredData}
                    shownData={shownData}
                    fetchMoreData={fetchMoreData}
                    isLeaderboardFetching={
                        isLeaderboardFetching && isLeaderboardUninitialized
                    }
                    pageSize={PAGE_SIZE}
                    currentPage={currentPage}
                    showLeader={showLeader}
                    shouldShowBettingOdds={shouldShowBettingOdds}
                    bettingOddsData={bettingOddsData}
                />
            </CurrentRoundContext.Provider>
        </SeasonContext.Provider>
    );
};

LeaderboardTableFetcher.propTypes = {
    cssClass: PropTypes.string,
    shouldHidePredicted: PropTypes.bool,
    title: PropTypes.string,
    startDateTime: PropTypes.string
};
